import { ClientId } from 'magpie-types';
import {
  WINDOW_BROKER_CONNECTOR_LOGGER_NAME,
  getAllowedOrigins,
} from '../../config';
import { RelayError } from '../../errors';
import { Logger } from 'simple-logging-lib';
import { MessageLink } from '../../models';
import { Subject } from 'reactive-lib/observable/subject';
import { WindowBrokerInterface } from '../window-broker-interface';
import { relayOperations } from './operations';

export class WindowBrokerConnector {
  static #instance: WindowBrokerConnector;
  private readonly relayPorts: Map<ClientId, MessagePort> = new Map();
  private readonly logger = Logger.getLogger(
    WINDOW_BROKER_CONNECTOR_LOGGER_NAME
  );
  private links: Set<MessageLink> = new Set();

  private constructor(private windowBroker: WindowBrokerInterface) {}

  static getInstance(
    windowBroker: WindowBrokerInterface
  ): WindowBrokerConnector {
    if (!WindowBrokerConnector.#instance) {
      WindowBrokerConnector.#instance = new WindowBrokerConnector(windowBroker);
    }
    return WindowBrokerConnector.#instance;
  }

  initialize = (): void => {
    window.addEventListener('message', this.onWindowMessage);
  };

  close = (): void => {
    window.removeEventListener('message', this.onWindowMessage);
    this.links.forEach((link) => link.complete());
    this.relayPorts.forEach((port) => {
      port.removeEventListener('message', this.handleOperation);
      port.close();
    });
    this.relayPorts.clear();

    // @ts-ignore - Resetting the singleton instance for testing purposes
    WindowBrokerConnector.#instance = undefined;
  };

  private onWindowMessage = (event: MessageEvent) => {
    if (!getAllowedOrigins().includes(event.origin)) {
      return;
    }
    if (relayOperations.probe.match(event.data)) {
      this.handleProbes(event);
    }
    if (relayOperations.handshake.match(event.data)) {
      this.handleHandshake(event).catch(this.logger.error);
    }
  };

  private handleProbes = (event: MessageEvent): void => {
    if (!event.source) {
      this.logger.debug('No source found in probe');
      return;
    }
    const replyTo = event.data.id;
    this.logger.debug(`Replying to probe ID "${replyTo}".`);
    const probeReplyMessage = relayOperations.probeReply({
      replyTo,
      brokerId: this.windowBroker.getBrokerId(),
    });
    event.source.postMessage(probeReplyMessage, { targetOrigin: event.origin });
  };

  private handleHandshake = async (event: MessageEvent): Promise<void> => {
    const link: MessageLink = new Subject();
    this.links.add(link);
    const requestId = event.data.id;
    const result = await this.windowBroker
      .handshake(event.data.payload, link)
      .catch(this.logger.error);
    if (!result) {
      return;
    }
    const { clientId } = result;
    const relayPort = event.ports[0];
    relayPort.start();
    relayPort.addEventListener('message', this.handleOperation);
    this.relayPorts.set(clientId, relayPort);
    relayPort.postMessage(
      relayOperations.handshakeResponse({ ...result, replyTo: requestId })
    );
    link.subscribe((message) =>
      relayPort.postMessage(relayOperations.relayMessage(message))
    );
  };

  private handleOperation = (event: MessageEvent): void => {
    const source = event.target as MessagePort;
    const confirmCompletion = () => {
      if (!source) {
        this.logger.debug('No source found in operation');
        return;
      }
      source.postMessage(
        relayOperations.opCompletedConfirmation(event.data.id)
      );
    };

    const handleError = (error: Error) => {
      this.logger.error(error);
      if (!source) {
        this.logger.debug('No source found in operation');
        return;
      }
      source.postMessage(
        relayOperations.opFailedConfirmation({ replyTo: event.data.id, error })
      );
    };

    if (relayOperations.topicSubscribe.match(event.data)) {
      this.windowBroker
        .topicSubscribe(event.data.payload)
        .then(confirmCompletion)
        .catch(handleError);
    } else if (relayOperations.topicUnsubscribe.match(event.data)) {
      this.windowBroker
        .topicUnsubscribe(event.data.payload)
        .then(confirmCompletion)
        .catch(handleError);
    } else if (relayOperations.getTopicSubscriptions.match(event.data)) {
      this.windowBroker
        .getTopicSubscriptions(event.data.payload)
        .then((subscriptions) => {
          const relayPort = this.relayPorts.get(event.data.payload.clientId);
          if (!relayPort) {
            throw new RelayError(RelayError.Reason.GET_TOPIC_SUBS_MISSING_PORT);
          }
          relayPort.postMessage(
            relayOperations.getTopicSubscriptionsResponse({
              ...subscriptions,
              replyTo: event.data.id,
            })
          );
        })
        .catch(handleError);
    } else if (relayOperations.close.match(event.data)) {
      this.windowBroker
        .close(event.data.payload.clientId)
        .then(confirmCompletion)
        .then(() => {
          this.close();
        })
        .catch(handleError);
    } else if (relayOperations.sendMessage.match(event.data)) {
      this.windowBroker
        .sendMessage(event.data.payload)
        .then(confirmCompletion)
        .catch(handleError);
    }
  };
}
